import type {
  ConformityRouteResult,
  ClinicalRequirementsResult,
  DeclarationOfConformity,
  UdiEudamedData,
} from '@/features/dossier/api';
import { EuTdRenderer } from './EuTdRenderer';
import { EuPmsRenderer } from './EuPmsRenderer';
import { EuSscpRenderer } from './EuSscpRenderer';
import { ConformityRouteRenderer } from './ConformityRoutePanel';
import { ClinicalRequirementsRenderer } from './ClinicalRequirementsPanel';
import { DeclarationOfConformityRenderer } from './DeclarationOfConformityPanel';
import { UdiEudamedDataRenderer } from './UdiEudamedDataPanel';
import { PsurRenderer } from './PsurRenderer';
import { ReferencesRenderer } from './ReferencesRenderer';
import {
  appliesToAllClasses,
  pickDossier,
  pickSubmissionHeader,
  InPdfNote,
  type EuDossierProfile,
} from './euDossierProfile';

/**
 * EU MDR (Regulation (EU) 2017/745) dossier profile — the on-screen mirror of
 * the backend `eu_combined_renderer`. Owns ONLY MDR Parts, titles, article
 * numbers, and applicability; it is wholly independent of the IVDR profile.
 *
 * Document order (Part 1..N):
 *   td → pms (Art 84) → SSCP (Art 32, Class III / IIb_implantable only) →
 *   conformity (Art 52) → clinical requirements (Art 61) → DoC → UDI.
 * Appendices: PSUR (Art 86) → implant card → SSCP lay summary → references.
 */

/** SSCP (Article 32) is required only for Class III and implantable Class IIb. */
const isImplantableOrIII = (deviceClass: string): boolean =>
  deviceClass === 'IIb_implantable' || deviceClass === 'III';

export const mdrDossierProfile: EuDossierProfile = {
  regulation: 'MDR',
  numberedParts: [
    {
      id: 'eu-sec-td',
      navTitle: 'Technical Documentation',
      title: 'Annex II Technical Documentation',
      applicable: appliesToAllClasses,
      pick: (p) => p.td,
      render: (part) => (
        <EuTdRenderer
          dossier={pickDossier(part) ?? {}}
          submissionHeader={pickSubmissionHeader(part) ?? undefined}
          hideCover
        />
      ),
    },
    {
      id: 'eu-sec-pms',
      navTitle: 'Post-Market Surveillance Plan',
      title: 'Annex III Post-Market Surveillance Plan (Article 84)',
      applicable: appliesToAllClasses,
      pick: (p) => p.pms,
      render: (part) => (
        <EuPmsRenderer
          dossier={pickDossier(part) ?? {}}
          submissionHeader={pickSubmissionHeader(part) ?? undefined}
          hideCover
        />
      ),
    },
    {
      id: 'eu-sec-sscp',
      navTitle: 'SSCP (Article 32)',
      title: 'Summary of Safety and Clinical Performance (Article 32)',
      applicable: isImplantableOrIII,
      pick: (p) => p.sscp,
      render: (part) => (
        <EuSscpRenderer
          dossier={pickDossier(part) ?? {}}
          submissionHeader={pickSubmissionHeader(part) ?? undefined}
          hideCover
        />
      ),
    },
    {
      id: 'eu-sec-conformity_route',
      navTitle: 'Conformity Route',
      title: 'Conformity Assessment Route (Article 52)',
      applicable: appliesToAllClasses,
      pick: (p) => p.conformity_route,
      render: (part, ctx) => (
        <ConformityRouteRenderer
          result={part as ConformityRouteResult}
          productId={ctx.productId}
          deviceClass={ctx.deviceClass}
          hideDownload
        />
      ),
    },
    {
      id: 'eu-sec-clinical_requirements',
      navTitle: 'Clinical Requirements',
      title: 'Clinical Requirements (Article 61)',
      applicable: appliesToAllClasses,
      pick: (p) => p.clinical_requirements,
      render: (part, ctx) => (
        <ClinicalRequirementsRenderer
          result={part as ClinicalRequirementsResult}
          productId={ctx.productId}
          deviceClass={ctx.deviceClass}
          hideDownload
        />
      ),
    },
    {
      id: 'eu-sec-declaration_of_conformity',
      navTitle: 'Declaration of Conformity',
      title: 'Declaration of Conformity (Annex IV)',
      applicable: appliesToAllClasses,
      pick: (p) => p.declaration_of_conformity,
      render: (part, ctx) => (
        <DeclarationOfConformityRenderer
          result={part as DeclarationOfConformity}
          productId={ctx.productId}
          deviceClass={ctx.deviceClass}
          regulation="MDR"
          hideDownload
        />
      ),
    },
    {
      id: 'eu-sec-udi_eudamed_data',
      navTitle: 'UDI / EUDAMED',
      title: 'UDI / EUDAMED Data (Annex VI)',
      applicable: appliesToAllClasses,
      pick: (p) => p.udi_eudamed_data,
      render: (part, ctx) => (
        <UdiEudamedDataRenderer
          result={part as UdiEudamedData}
          productId={ctx.productId}
          deviceClass={ctx.deviceClass}
          regulation="MDR"
          hideDownload
        />
      ),
    },
  ],
  appendices: [
    {
      id: 'eu-sec-psur',
      navTitle: 'PSUR (Article 86)',
      title: 'Periodic Safety Update Report (Article 86)',
      applicable: appliesToAllClasses,
      pick: (p) => p.psur ?? null,
      render: (part) => <PsurRenderer psur={part as Record<string, unknown>} />,
    },
    {
      id: 'eu-sec-implant_card',
      navTitle: 'Implant Card',
      title: 'Implant Card & Patient Information (Article 18)',
      applicable: appliesToAllClasses,
      pick: (p) => p.implant_card ?? null,
      render: () => <InPdfNote title="Implant Card & Patient Information (Article 18)" />,
    },
    {
      id: 'eu-sec-sscp_lay',
      navTitle: 'SSCP — Lay Summary',
      title: 'SSCP — Patient / Lay Summary',
      applicable: appliesToAllClasses,
      pick: (p) => p.sscp_lay ?? null,
      render: () => <InPdfNote title="SSCP — Patient / Lay Summary" />,
    },
    {
      id: 'eu-sec-references',
      navTitle: 'References',
      title: 'References',
      applicable: appliesToAllClasses,
      pick: (p) => (p.references && p.references.length > 0 ? p.references : null),
      render: (part) => <ReferencesRenderer references={part as string[]} />,
    },
  ],
};
